"use client";

import { VehicleMedia } from "@alba-cars/common-modules";
import dynamic from "next/dynamic";

import { getImageMedia } from "./videoUtils";
import OptimizedImage from "@/components/common/images/OptimizedImage";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { DEFAULT_PLACEHOLDER } from "@/utils/constants";

import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/thumbs";

interface DesktopGalleryProps {
  images: VehicleMedia[];
  altText: string;
  onImageClickAction: (index: number) => void;
  hasVerticalVideo?: boolean;
  onVerticalVideoOpenAction?: () => void;
  isReserved?: boolean;
  isCarOfTheDay?: boolean;
  imageObjectFit?: "cover" | "contain";
}

const SwiperGallery = dynamic(
  async () => {
    const SwiperReact = await import("swiper/react");
    const { FreeMode, Navigation, Thumbs, Virtual } = await import("swiper/modules");
    const { useRef, useState, useCallback } = await import("react");
    const { useTranslations } = await import("next-intl");
    const OptimizedImage = (await import("@/components/common/images/OptimizedImage")).default;
    const VideoPlayer = (await import("@/components/common/VideoPlayer")).default;
    const IconArrowRight = (await import("@/components/Icons/IconArrowRight")).default;
    const { Play, Pause, Smartphone } = await import("lucide-react");
    const { formatImageUrl } = await import("@/lib/utils");
    const { DEFAULT_PLACEHOLDER } = await import("@/utils/constants");
    const { extractHorizontalVideo, getImageMedia } = await import("./videoUtils");

    return function SwiperGalleryInner({
      images,
      altText,
      onImageClickAction,
      hasVerticalVideo = false,
      onVerticalVideoOpenAction,
      isReserved = false,
      isCarOfTheDay = false,
      imageObjectFit = "cover",
    }: DesktopGalleryProps) {
      const tGallery = useTranslations("CarDetails.gallery");
      const [thumbsSwiper, setThumbsSwiper] = useState<any>(null);
      const [activeIndex, setActiveIndex] = useState(0);
      const [isPlaying, setIsPlaying] = useState(true);
      const swiperRef = useRef<any>(null);
      const thumbsSwiperRef = useRef<any>(null);
      const videoRef = useRef<HTMLVideoElement>(null);

      const horizontalVideo = extractHorizontalVideo(images as any[]);
      const imageOnlyMedia = getImageMedia(images as any[]);
      const videoOffset = horizontalVideo ? 1 : 0;
      const cdnBase = process.env.NEXT_PUBLIC_S3_URL ?? "";
      const shouldShowVerticalTrigger = hasVerticalVideo && !(horizontalVideo && activeIndex === 0);
      const isContainFit = imageObjectFit === "contain";
      const mainImageObjectPosition = isContainFit ? "object-center" : "object-bottom";
      const mainImageFitClass = isContainFit ? "object-contain" : "object-cover";

      const handleVideoToggle = () => {
        const v = videoRef.current;
        if (!v) return;
        if (v.paused) {
          v.play();
          setIsPlaying(true);
        } else {
          v.pause();
          setIsPlaying(false);
        }
      };

      const centerThumb = useCallback((index: number, swiper?: any) => {
        const thumbs = swiper ?? thumbsSwiperRef.current;
        if (!thumbs || thumbs.destroyed) return;

        const slidesPerView: number =
          typeof thumbs.params.slidesPerView === "number"
            ? thumbs.params.slidesPerView
            : (thumbs.slidesPerViewDynamic?.() ?? 3);

        const offset = Math.floor(slidesPerView / 2);
        const targetIndex = Math.max(0, index - offset);

        if (thumbs.params.loop) {
          thumbs.slideToLoop(targetIndex, 300);
        } else {
          thumbs.slideTo(targetIndex, 300);
        }
      }, []);

      const handleSlideClick = (index: number): void => {
        if (horizontalVideo && index === 0) return;
        onImageClickAction(index - videoOffset);
      };

      const handleThumbnailClick = (index: number): void => {
        setActiveIndex(index);
        if (swiperRef.current) {
          swiperRef.current.slideToLoop(index);
        }
        centerThumb(index);
      };

      const handleSlideChange = (swiper: any): void => {
        const idx: number = swiper.realIndex;
        setActiveIndex(idx);
        centerThumb(idx);
      };

      return (
        <div className="group relative z-0 hidden lg:block">
          {/* ── Main swiper ── */}
          <div
            className={`relative mb-4 h-full w-full overflow-hidden rounded-3xl lg:h-[600px] ${
              isContainFit ? "bg-neutral-100" : ""
            }`}
          >
            {isReserved && (
              <div className="absolute left-3 top-3 z-50 flex flex-wrap items-center gap-2 lg:max-w-56 2xl:max-w-full">
                <div className="inter rounded-lg px-3 py-1 text-center text-xs font-semibold bg-primary text-white">
                  {isCarOfTheDay ? tGallery("carOfTheDay") : tGallery("reserved")}
                </div>
              </div>
            )}
            <SwiperReact.Swiper
              onSwiper={(swiper: any) => (swiperRef.current = swiper)}
              spaceBetween={0}
              direction="horizontal"
              navigation={{
                nextEl: ".custom-swiper-button-next",
                prevEl: ".custom-swiper-button-prev",
              }}
              loop={true}
              thumbs={{ swiper: thumbsSwiper }}
              modules={[Navigation, Thumbs, Virtual]}
              preventInteractionOnTransition={true}
              virtual={{
                addSlidesAfter: 1,
                addSlidesBefore: 1,
                enabled: true,
              }}
              onSlideChange={handleSlideChange}
              className="h-full w-full"
            >
              {/* ── Video slide (first) — disabled via extractHorizontalVideo ── */}
              {horizontalVideo && (
                <SwiperReact.SwiperSlide key="desktop-video-slide" virtualIndex={0}>
                  <div className="relative h-full w-full bg-black">
                    <VideoPlayer
                      s3Url={horizontalVideo.src}
                      cdnBase={cdnBase}
                      videoRef={videoRef}
                      startLevel={2}
                      capLevelToPlayerSize={false}
                      className="h-full w-full object-contain"
                    />
                  </div>
                </SwiperReact.SwiperSlide>
              )}

              {/* ── Image slides ── */}
              {imageOnlyMedia.map((image, index) => (
                <SwiperReact.SwiperSlide
                  key={image.id || index}
                  className="h-full w-full"
                  virtualIndex={index + videoOffset}
                >
                  <div
                    className="relative h-full w-full cursor-pointer"
                    role="button"
                    tabIndex={0}
                    onClick={() => handleSlideClick(index + videoOffset)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleSlideClick(index + videoOffset);
                      }
                    }}
                  >
                    <OptimizedImage
                      src={formatImageUrl(image.mediaUrl) ?? DEFAULT_PLACEHOLDER}
                      alt={`${altText}-${index + 1}`}
                      fill
                      priority={index < 3}
                      quality={50}
                      fetchPriority={index < 3 ? "high" : "auto"}
                      className={`h-full w-full ${mainImageFitClass} ${mainImageObjectPosition}`}
                      objectFit={imageObjectFit}
                      imageLayout="custom"
                      sizes="(max-width: 1280px) 100vw, 1280px"
                      placeholder={"blur"}
                      blurDataURL={DEFAULT_PLACEHOLDER}
                      loading={index < 3 ? "eager" : "lazy"}
                    />
                  </div>
                </SwiperReact.SwiperSlide>
              ))}

              {/* Vertical video trigger — disabled via extractVerticalVideo / Gallery props */}
              {shouldShowVerticalTrigger && (
                <button
                  type="button"
                  className="absolute bottom-4 right-4 z-30 flex animate-shake items-center gap-2 rounded-full bg-black/70 px-4 py-3 font-inter text-xs font-semibold text-white transition-opacity [animation-duration:1.5s] [animation-iteration-count:infinite] [animation-timing-function:ease-in-out] hover:opacity-90"
                  // className="absolute bottom-4 right-4 z-30 flex items-center gap-2 rounded-full bg-black/70 px-4 py-3 font-inter text-xs font-semibold text-white transition-opacity hover:opacity-90"
                  onClick={onVerticalVideoOpenAction}
                  aria-label="Open vertical video"
                >
                  <Smartphone size={14} />
                  {tGallery("playVideo")}
                </button>
              )}

              {/* Nav arrows */}
              <div className="custom-swiper-button-prev fade-in fade-out absolute left-0 top-0 z-20 flex h-full w-10 cursor-pointer items-center justify-center rounded-bl-2xl rounded-tl-2xl bg-black/40 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                <button aria-label="Show previous image">
                  <IconArrowRight fill="white" width="25" height="25" className="-rotate-180" />
                </button>
              </div>
              <div className="custom-swiper-button-next fade-out absolute right-0 top-0 z-20 flex h-full w-10 cursor-pointer items-center justify-center rounded-br-2xl rounded-tr-2xl bg-black/40 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                <button aria-label="Show next image">
                  <IconArrowRight fill="white" width="25" height="25" />
                </button>
              </div>
            </SwiperReact.Swiper>
          </div>

          {/* ── Thumbnail swiper ── */}
          <div className="relative h-32 w-full overflow-hidden rounded-xl">
            <SwiperReact.Swiper
              spaceBetween={8}
              breakpoints={{
                1024: { slidesPerView: 3 },
                1300: { slidesPerView: 3, spaceBetween: 20 },
                1880: { slidesPerView: 5 },
              }}
              freeMode={true}
              watchSlidesProgress={true}
              direction="horizontal"
              modules={[FreeMode, Navigation, Thumbs]}
              className="h-full"
              preventInteractionOnTransition={true}
              loop={true}
              onSwiper={(swiper: any) => {
                thumbsSwiperRef.current = swiper;
                setThumbsSwiper(swiper);
              }}
            >
              {/* ── Video thumbnail — disabled via extractHorizontalVideo ── */}
              {horizontalVideo && (
                <SwiperReact.SwiperSlide key="thumb-video">
                  <div
                    className={`relative h-full w-full cursor-pointer overflow-hidden rounded-2xl border-2 ${
                      activeIndex === 0 ? "border-primary" : "border-transparent"
                    }`}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      handleThumbnailClick(0);
                      handleVideoToggle();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        handleThumbnailClick(0);
                        handleVideoToggle();
                      }
                    }}
                  >
                    <div className="relative h-full w-full">
                      {imageOnlyMedia[0] && (
                        <OptimizedImage
                          src={formatImageUrl(imageOnlyMedia[0].mediaUrl) ?? DEFAULT_PLACEHOLDER}
                          alt={`${altText}-video-thumbnail`}
                          width={200}
                          height={200}
                          quality={40}
                          className="h-full w-full object-cover object-bottom"
                          imageLayout="custom"
                          sizes="200px"
                          loading="lazy"
                          blurDataURL={imageOnlyMedia[0]?.placeholder ?? DEFAULT_PLACEHOLDER}
                        />
                      )}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary">
                          {isPlaying ? (
                            <Pause color="white" size={18} fill="white" />
                          ) : (
                            <Play color="white" size={18} fill="white" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </SwiperReact.SwiperSlide>
              )}

              {/* ── Image thumbnails ── */}
              {imageOnlyMedia.map((image, index) => {
                const thumbIndex = index + videoOffset;
                return (
                  <SwiperReact.SwiperSlide key={`thumb-${image.id}-${index}`} className="h-full">
                    <div
                      className={`relative h-full w-full cursor-pointer overflow-hidden rounded-2xl border-2 ${
                        activeIndex === thumbIndex ? "border-primary" : "border-transparent"
                      }`}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleThumbnailClick(thumbIndex)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") handleThumbnailClick(thumbIndex);
                      }}
                    >
                      <OptimizedImage
                        src={formatImageUrl(image.mediaUrl) ?? DEFAULT_PLACEHOLDER}
                        alt={`${altText}-thumbnail-${index + 1}`}
                        width={200}
                        height={200}
                        quality={40}
                        className="h-full w-full object-cover object-bottom"
                        imageLayout="custom"
                        sizes="200px"
                        loading="lazy"
                        blurDataURL={image?.placeholder ?? DEFAULT_PLACEHOLDER}
                      />
                    </div>
                  </SwiperReact.SwiperSlide>
                );
              })}
            </SwiperReact.Swiper>
          </div>
        </div>
      );
    };
  },
  {
    ssr: false,
    loading: () => <DesktopGalleryLoader />,
  },
);

export default function DesktopGallery({
  images,
  altText,
  onImageClickAction,
  hasVerticalVideo,
  onVerticalVideoOpenAction,
  isReserved,
  isCarOfTheDay = false,
  imageObjectFit = "cover",
}: DesktopGalleryProps) {
  const isLaptopOrLarger = useMediaQuery("(min-width: 1024px)");

  if (!isLaptopOrLarger) {
    return null;
  }

  return (
    <SwiperGallery
      images={images}
      altText={altText}
      onImageClickAction={onImageClickAction}
      hasVerticalVideo={hasVerticalVideo}
      onVerticalVideoOpenAction={onVerticalVideoOpenAction}
      isReserved={isReserved}
      isCarOfTheDay={isCarOfTheDay}
      imageObjectFit={imageObjectFit}
    />
  );
}

const DesktopGalleryLoader = ({ images, altText }: { images?: VehicleMedia[]; altText?: string }) => {
  const imageOnlyMedia = images && images.length > 0 ? getImageMedia(images as any[]).slice(0, 5) : [];

  const mainImage =
    imageOnlyMedia.length > 0 ? (imageOnlyMedia[0]?.mediaUrl ?? DEFAULT_PLACEHOLDER) : DEFAULT_PLACEHOLDER;

  const thumbnails: string[] =
    imageOnlyMedia.length > 0
      ? imageOnlyMedia.map((img) => img.mediaUrl)
      : (Array.from({ length: 5 }).fill(DEFAULT_PLACEHOLDER) as string[]);

  return (
    <div className="group relative z-0 hidden lg:block">
      <div className="relative mb-4 flex h-full w-full items-center justify-center overflow-hidden rounded-3xl bg-gray-100 lg:h-[600px]">
        <OptimizedImage
          src={mainImage}
          alt={altText ?? "Loading gallery"}
          fill
          className="h-full w-full object-contain object-bottom"
          imageLayout="custom"
          sizes="(max-width: 1280px) 100vw, 1280px"
          placeholder={"blur"}
          blurDataURL={DEFAULT_PLACEHOLDER}
        />
      </div>
      <div className="relative flex h-32 w-full items-center justify-center overflow-hidden rounded-xl bg-gray-100">
        <div className="flex h-full w-full gap-2 px-2">
          {thumbnails.map((thumbnail, index) => (
            <div
              key={index}
              className="relative h-full w-64 flex-shrink-0 flex-grow overflow-hidden rounded-2xl border-2 border-transparent xxl:w-52"
            >
              <OptimizedImage
                src={thumbnail.length > 0 ? (thumbnail ?? DEFAULT_PLACEHOLDER) : DEFAULT_PLACEHOLDER}
                alt={`Loading thumbnail ${index + 1}`}
                width={80}
                height={80}
                className="h-full w-full object-cover object-bottom"
                imageLayout="custom"
                sizes="80px"
                placeholder="blur"
                blurDataURL={DEFAULT_PLACEHOLDER}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
